import { API_URL } from 'config';
import { axios } from 'helpers/custom-axios';
import {
  Event,
  EventAction,
  EventStatus,
  EventVerificationForAdmins,
  EventVerificationForOrganizers,
  Participant,
  ResponseData,
  StudentDocumentForOrganizers,
} from 'types';

const EventCoreService = {
  createEvent: async (event: {
    title: string;
    description: string;
    location: string;
    startAt: number;
    endAt: number;
  }) => {
    return axios.post<ResponseData<Event>>(`${API_URL}admin/events/`, event);
  },

  deleteEvent: async (eventId: string) => {
    return axios.put<ResponseData<Event>>(`${API_URL}events/${eventId}/delete`);
  },

  updateEventStatus: async (eventId: string, status: EventStatus) => {
    return axios.put<ResponseData<EventStatus>>(`${API_URL}events/${eventId}/status`, {
      status,
    });
  },

  restoreEvent: async (eventId: string) => {
    return axios.put<ResponseData<EventStatus>>(`${API_URL}events/${eventId}/status/restore`);
  },

  uploadPoster: async (eventId: string, file: File) => {
    const formData = new FormData();
    formData.append('eventId', eventId);
    formData.append('poster', file);

    return axios.post<ResponseData<string>>(`${API_URL}events/poster`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  uploadDisplayBackground: async (eventId: string, file: File) => {
    const formData = new FormData();
    formData.append('eventId', eventId);
    formData.append('background', file);

    return axios.post<ResponseData<string>>(
      `${API_URL}events/display-settings/background`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },

  getEventById: async (eventId: string) => {
    return axios.get<ResponseData<Event>>(`${API_URL}events/${eventId}`);
  },

  updateEventMetadata: async (
    eventId: string,
    title: string,
    description: string,
    location: string,
    startAt: number,
    endAt: number
  ) => {
    return axios.put<ResponseData<Event>>(`${API_URL}events/${eventId}/metadata`, {
      title,
      description,
      location,
      startAt,
      endAt,
    });
  },

  getParticipatedEvents: async (
    order: 'asc' | 'desc',
    limit: number,
    offset: number,
    title?: string,
    status?: EventStatus,
    createdBy?: string
  ) => {
    return axios.get<ResponseData<{ eventList: Array<Event>; count: number }>>(
      `${API_URL}events/participated?sortBy=createdAt&order=${order}&limit=${limit}&offset=${offset}${
        title ? `&title=${title}` : ''
      }${status ? `&status=${status}` : ''}${createdBy ? `&createdBy=${createdBy}` : ''}`
    );
  },

  getEventActions: async (eventId: string, limit: number, offset: number) => {
    return axios.get<ResponseData<Array<EventAction>>>(
      `${API_URL}events/${eventId}/actions?limit=${limit}&offset=${offset}`
    );
  },

  submitReview: async (verificationId: string, isVerified: boolean, comment: string) => {
    await axios.post(`${API_URL}admin/events/verification/${verificationId}/review`, {
      isVerified,
      comment,
    });
  },

  uploadDisplayOptions: async (
    eventId: string,
    timeDisplay: 'current' | 'countdown',
    participantsDisplay1: 'current' | 'total' | 'percent',
    participantsDisplay2: 'current' | 'total' | 'percent'
  ) => {
    await axios.post(`${API_URL}events/${eventId}/display-settings/options`, {
      timeDisplay,
      participantsDisplay1,
      participantsDisplay2,
    });
  },

  getEventVerificationById: async (verificationId: string) => {
    return axios.get<ResponseData<EventVerificationForAdmins>>(
      `${API_URL}admin/events/verifications/${verificationId}`
    );
  },

  getParticipantsOfEventVerification: async ({
    verificationId,
    limit,
    offset,
  }: {
    verificationId: string;
    limit: number;
    offset: number;
  }) => {
    return axios.get<
      ResponseData<{
        participantList: Array<Participant>;
        count: number;
      }>
    >(
      `${API_URL}admin/events/verifications/${verificationId}/participants?limit=${limit}&offset=${offset}`
    );
  },

  getEventVerificationByIdForUser: async (eventId: string) => {
    return axios.get<ResponseData<EventVerificationForOrganizers>>(
      `${API_URL}events/${eventId}/verifications`
    );
  },

  submitVerificationUser: async ({ eventId, comment }: { eventId: string; comment?: string }) => {
    const body: { comment?: string } = {};

    if (comment && comment.trim() !== '') {
      body.comment = comment;
    }
    return axios.post<ResponseData<EventVerificationForOrganizers>>(
      `${API_URL}events/${eventId}/verification/submission`,
      body
    );
  },

  getVerificationEvents: async ({
    order,
    limit,
    offset,
    filter,
  }: {
    order: 'asc' | 'desc';
    filter: boolean;
    limit: number;
    offset: number;
  }) => {
    return axios.get<
      ResponseData<{
        verifications: EventVerificationForAdmins[];
        count: number;
      }>
    >(
      `${API_URL}admin/events/verifications?isVerified=${String(filter)}&order=${
        order ?? 'desc'
      }&limit=${limit}&offset=${offset}`
    );
  },

  getStudentByStundentId: async (studentId: string) => {
    return axios.get<ResponseData<StudentDocumentForOrganizers>>(
      `${API_URL}events/student/${studentId}`
    );
  },
};

export default EventCoreService;
